/**
 * PurchaseOrderDetailModal — COM-02/03: Ver detalle de OC + GoodsReceipt
 */
import { useState } from 'react'
import { Package, CheckCircle2, ClipboardList, Loader2, FileText, UserCheck, FileSpreadsheet, ArrowRight } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  usePurchaseOrder, useGoodsReceipts, downloadPurchaseOrderPdf,
  useRequestPurchaseOrderApproval, useApprovePurchaseOrder, useConvertOrderToInvoice,
  type PurchaseOrderStatus,
} from '../hooks/usePurchases'
import { formatDate, formatDateTime, formatNumber, useTenantLocale } from '@/lib/locale'
import { RequestApprovalModal } from './RequestApprovalModal'
import { ConvertToInvoiceModal } from './ConvertToInvoiceModal'
import { usePermission } from '@/hooks/usePermission'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { Modal } from '@/components/ds/Modal'

const STATUS_CONFIG: Record<PurchaseOrderStatus, { label: string; cls: string; dotCls: string }> = {
  draft:     { label: 'Borrador',   cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
  approved:  { label: 'Aprobada',   cls: 'bg-warning-surface text-warning-content',     dotCls: 'bg-warning-solid' },
  sent:      { label: 'Enviada',    cls: 'bg-primary-50 text-primary-hover',       dotCls: 'bg-primary/60' },
  received:  { label: 'Recibida',   cls: 'bg-success-surface text-success-content', dotCls: 'bg-emerald-400' },
  cancelled: { label: 'Anulada',    cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
}

function StatusBadge({ status }: { status: PurchaseOrderStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

interface Props {
  orderId: string
  onClose: () => void
}

export function PurchaseOrderDetailModal({ orderId, onClose }: Props) {
  const tax = useTenantTax()
  const { data: order, isLoading } = usePurchaseOrder(orderId)
  const { data: receiptsData } = useGoodsReceipts({})
  const receipt = receiptsData?.receipts.find(r => r.purchase_order === orderId)
  const locale = useTenantLocale()
  const currency = order?.currency ?? 'CLP'
  const decimals = currency === 'CLP' ? 0 : 2
  const fmtAmount = (v: string | number) =>
    formatNumber(typeof v === 'string' ? parseFloat(v) || 0 : v, locale, decimals)
  const fmtAmountWithCurrency = (v: string | number) => (
    <><span className="text-content-subtle text-xs mr-1">{currency}</span>{fmtAmount(v)}</>
  )
  const { can } = usePermission()

  const [requestModalOpen, setRequestModalOpen] = useState(false)
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const requestApproval = useRequestPurchaseOrderApproval()
  const approve = useApprovePurchaseOrder()
  const convertToInvoice = useConvertOrderToInvoice()

  const handleApprove = () => {
    if (!order) return
    setActionError(null)
    approve.mutate(order.id, {
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setActionError(err.response?.data?.error?.message ?? 'No se pudo aprobar la orden.')
      },
    })
  }

  if (isLoading) {
    return (
      <Modal open onClose={onClose} size="sm" title="Orden de Compra">
        <div className="flex items-center gap-3">
          <Loader2 size={20} className="animate-spin text-primary" />
          <span className="text-sm text-content-muted">Cargando detalle...</span>
        </div>
      </Modal>
    )
  }

  if (!order) return null

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={
      <span>
        Orden de Compra
        {order.order_number && (
          <span className="ml-2 font-mono text-content-muted">#{order.order_number}</span>
        )}
      </span>
    }
        subtitle={<StatusBadge status={order.status} />}
        actions={
          <>
            {order.status !== 'cancelled' && (
              <button
                onClick={() => downloadPurchaseOrderPdf(order.id, `${order.order_number || order.id}.pdf`)}
                className="mr-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-primary border border-primary/30 rounded-lg hover:bg-primary-50 transition-colors"
                title="Descargar PDF"
              >
                <FileText size={13} />
                Descargar PDF
              </button>
            )}
          </>
        }
      >
        <div className="space-y-6">
          {/* Info general */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-content-muted mb-0.5">Proveedor</p>
              <p className="font-medium text-content">{order.supplier_name}</p>
            </div>
            <div>
              <p className="text-xs text-content-muted mb-0.5">Fecha de Orden</p>
              <p className="text-content">{formatDate(order.order_date, locale)}</p>
            </div>
            {order.expected_date && (
              <div>
                <p className="text-xs text-content-muted mb-0.5">Fecha Esperada</p>
                <p className="text-content">{formatDate(order.expected_date, locale)}</p>
              </div>
            )}
            {order.notes && (
              <div className="col-span-2">
                <p className="text-xs text-content-muted mb-0.5">Notas</p>
                <p className="text-content whitespace-pre-wrap">{order.notes}</p>
              </div>
            )}
            {order.payment_terms && (
              <div className="col-span-2">
                <p className="text-xs text-content-muted mb-0.5">Términos y condiciones</p>
                <p className="text-content whitespace-pre-wrap">{order.payment_terms}</p>
              </div>
            )}
            {order.expense_account_display && (
              <div className="col-span-2">
                <p className="text-xs text-content-muted mb-0.5">Cuenta de gasto</p>
                <p className="text-content text-sm">{order.expense_account_display}</p>
              </div>
            )}
            {order.cancellation_reason && (
              <div className="col-span-2">
                <p className="text-xs text-content-muted mb-0.5">Motivo de Anulación</p>
                <p className="text-danger-content-soft text-sm">{order.cancellation_reason}</p>
              </div>
            )}
          </div>

          {actionError && (
            <div className="px-4 py-3 bg-danger-surface border border-danger-border rounded-lg text-sm text-danger-content">
              {actionError}
            </div>
          )}

          {/* Panel aprobación */}
          {(order.approver_name || order.approved_by_name || order.status === 'draft') && (
            <div className="border border-divider rounded-xl p-4 bg-row-alt/40">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck size={14} className="text-content-subtle" />
                <h3 className="text-sm font-medium text-content">Aprobación</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-content-muted mb-0.5">Aprobador asignado</p>
                  <p className="text-content">{order.approver_name ?? '—'}</p>
                </div>
                {order.requested_approval_at && (
                  <div>
                    <p className="text-xs text-content-muted mb-0.5">Solicitada el</p>
                    <p className="text-content">{formatDateTime(order.requested_approval_at, locale)}</p>
                  </div>
                )}
                {order.approved_by_name && (
                  <>
                    <div>
                      <p className="text-xs text-content-muted mb-0.5">Aprobada por</p>
                      <p className="text-content">{order.approved_by_name}</p>
                    </div>
                    {order.approved_at && (
                      <div>
                        <p className="text-xs text-content-muted mb-0.5">Aprobada el</p>
                        <p className="text-content">{formatDateTime(order.approved_at, locale)}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              {order.status === 'draft' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {can('purchases.orders', 'edit') && (
                    <button
                      onClick={() => { setActionError(null); setRequestModalOpen(true) }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface transition-colors"
                    >
                      <UserCheck size={13} />
                      {order.approver_name ? 'Reasignar aprobador' : 'Solicitar aprobación'}
                    </button>
                  )}
                  {can('purchases.orders', 'approve') && (
                    <button
                      onClick={handleApprove}
                      disabled={approve.isPending}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface transition-colors disabled:opacity-60"
                    >
                      {approve.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      Aprobar
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Factura generada */}
          {order.invoice ? (
            <div className="border border-primary/30 rounded-xl p-4 bg-primary-50 dark:bg-primary-900/30">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet size={14} className="text-primary" />
                <h3 className="text-sm font-medium text-primary">Factura generada</h3>
              </div>
              <p className="text-sm text-content">
                #{order.invoice.invoice_number} — {order.invoice.status_display} — Total {fmtAmount(order.invoice.total)}
              </p>
            </div>
          ) : (
            ['approved', 'sent', 'received'].includes(order.status) && can('purchases.invoices', 'create') && (
              <button
                onClick={() => { setActionError(null); setConvertModalOpen(true) }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
              >
                <ArrowRight size={14} />
                Convertir a factura de proveedor
              </button>
            )
          )}

          {/* Líneas */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList size={14} className="text-content-subtle" />
              <h3 className="text-sm font-medium text-content">Líneas de la Orden</h3>
            </div>
            <DataTable
              columns={[
                { key: 'sku', header: 'SKU' },
                { key: 'name', header: 'Descripción', render: l => <span className="text-content">{l.name}</span> },
                { key: 'quantity', header: 'Cantidad', numeric: true },
                { key: 'unit_price', header: 'P. Neto', numeric: true, render: l => fmtAmount(l.unit_price) },
                {
                  key: 'subtotal', header: 'Subtotal', numeric: true,
                  render: l => <span className="font-medium text-content">{fmtAmount(l.subtotal ?? '0')}</span>,
                },
              ]}
              rows={order.lines}
              rowKey={(_, i) => i}
              minWidth={560}
              caption="Líneas de la orden de compra"
            />
          </div>

          {/* Totales */}
          <div className="bg-glass-3 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between text-content-muted">
              <span>Subtotal neto</span>
              <span>{fmtAmountWithCurrency(order.subtotal)}</span>
            </div>
            <div className="flex justify-between text-content-muted">
              <span>{tax.name}</span>
              <span>{fmtAmountWithCurrency(order.tax_amount)}</span>
            </div>
            <div className="flex justify-between font-semibold text-content border-t border-divider pt-2">
              <span>Total</span>
              <span>{fmtAmountWithCurrency(order.total)}</span>
            </div>
          </div>

          {/* GoodsReceipt */}
          {receipt && (
            <div className="border border-success-border rounded-xl p-4 bg-success-surface">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={15} className="text-success-content-soft" />
                <h3 className="text-sm font-medium text-success-content-strong">Recepción de Mercancía</h3>
              </div>
              <p className="text-xs text-success-content mb-2">
                Recibido el {formatDateTime(receipt.received_at, locale)}
              </p>
              <div className="space-y-1">
                {receipt.lines.map((line, i) => (
                  <div key={i} className="flex justify-between text-xs text-success-content">
                    <span>{line.sku} — {line.name}</span>
                    <span>{line.quantity} u.</span>
                  </div>
                ))}
              </div>
              {receipt.notes && (
                <p className="text-xs text-success-content-soft mt-2 border-t border-success-border pt-2">{receipt.notes}</p>
              )}
            </div>
          )}

          {order.status === 'received' && !receipt && (
            <div className="flex items-center gap-2 text-sm text-content-muted">
              <Package size={14} />
              <span>Cargando información de recepción...</span>
            </div>
          )}
        </div>
      </Modal>
      <RequestApprovalModal
          open={requestModalOpen}
          orderNumber={order.order_number}
          onClose={() => setRequestModalOpen(false)}
          isPending={requestApproval.isPending}
          onConfirm={(approverId) => {
            requestApproval.mutate({ id: order.id, approverId }, {
              onSuccess: () => setRequestModalOpen(false),
              onError: (e: unknown) => {
                const err = e as { response?: { data?: { error?: { message?: string } } } }
                setActionError(err.response?.data?.error?.message ?? 'No se pudo solicitar la aprobación.')
                setRequestModalOpen(false)
              },
            })
          }}
        />
      <ConvertToInvoiceModal
          open={convertModalOpen}
          orderNumber={order.order_number}
          defaultExpenseAccountId={order.expense_account}
          defaultExpenseAccountLabel={order.expense_account_display}
          onClose={() => setConvertModalOpen(false)}
          isPending={convertToInvoice.isPending}
          onConfirm={(data) => {
            convertToInvoice.mutate({ id: order.id, ...data }, {
              onSuccess: () => setConvertModalOpen(false),
              onError: (e: unknown) => {
                const err = e as { response?: { data?: { error?: { message?: string } } } }
                setActionError(err.response?.data?.error?.message ?? 'No se pudo convertir la OC.')
                setConvertModalOpen(false)
              },
            })
          }}
        />
    </>
  )
}
